import classNames from 'classnames';
import {isNil} from 'lodash';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {WithRouterProps} from 'react-router';

import Assignment from './Assignment';
import FilterDropdown, {Value as DropdownValue} from './FilterDropdown';
import {LoadingSpinner} from '@albert-io/atomic';
import TabEmptyState from '../TabEmptyState';
import useAssignments from './hooks/useAssignments';
import {useStudentDashboard} from 'client/Classrooms/StudentDashboard/StudentDashboard.store';

export default function Assignments({params}: WithRouterProps) {
  const areClassroomsLoading = useStudentDashboard((state) => state.areClassroomsLoading);

  const classroomId: string | undefined = params.classroomId;

  const paginationTrigger = useRef<HTMLDivElement | null>(null);

  const [dropdownValue, setDropdownValue] = useState<DropdownValue>('toDo');

  // This query will always get open assignments regardless of the current dropdown state in order to
  // provide an accurate empty state for "To-Do" assignments
  const {assignments: openAssignments, loading: loadingAll} = useAssignments(classroomId, 'open');

  const {assignments, fetchMore, isLoadingMore, loading, resetMoreAssignments} = useAssignments(
    classroomId,
    dropdownValue
  );

  function onDropdownValueChange(newDropdownValue: DropdownValue) {
    setDropdownValue(newDropdownValue);
    resetMoreAssignments();
  }

  const isLoading = areClassroomsLoading || loading || loadingAll;

  const fetchMoreRef = useRef(fetchMore);
  fetchMoreRef.current = fetchMore;

  const stableHandleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const entry = entries[0];
      if (dropdownValue !== 'toDo' && entry?.isIntersecting) {
        fetchMoreRef.current();
      }
    },
    [dropdownValue]
  );

  useEffect(() => {
    const localPaginationTrigger = paginationTrigger?.current;
    const root = document.querySelector('#tabContentContainer');

    const observer = new IntersectionObserver(stableHandleIntersection, {
      root,
      rootMargin: '0px 0px 200px 0px',
      threshold: 0
    });

    if (!isNil(localPaginationTrigger)) {
      observer.observe(localPaginationTrigger);
    }

    return () => {
      if (!isNil(localPaginationTrigger)) {
        observer.unobserve(localPaginationTrigger);
        observer.disconnect();
      }
    };
  }, [stableHandleIntersection]);

  return (
    <>
      <div className='relative flex flex-col gap-x2 overflow-hidden px-x1 py-x3 m:px-x4'>
        <FilterDropdown
          className='mb-x1'
          disabled={isLoading}
          isScopedToClassroom={classroomId !== undefined}
          onValueChange={onDropdownValueChange}
          value={dropdownValue}
        />
        {isLoading && <LoadingSpinner bottomMarginLevel={0} size={3} />}
        {!isLoading && assignments.length === 0 && (
          <TabEmptyState
            copy={
              dropdownValue === 'toDo' && openAssignments.length > 0
                ? 'No upcoming assignments'
                : 'Your assignments will appear here'
            }
            icon={['fal', 'file-alt']}
          />
        )}
        {!isLoading &&
          assignments.length > 0 &&
          assignments.map((assignment) => (
            <Assignment assignment={assignment} key={assignment.assignmentId} />
          ))}
        {!isLoading && (
          <div
            className={classNames(
              'absolute flex w-full justify-center transition-[bottom] duration-200',
              {
                'bottom-x3': isLoadingMore,
                '-bottom-x7': !isLoadingMore
              }
            )}
          >
            <div className='flex h-x7 w-x7 items-center rounded-full background-subtle p-x1 elevation-shadow-l2'>
              <LoadingSpinner bottomMarginLevel={0} size={2} />
            </div>
          </div>
        )}
      </div>
      <div ref={paginationTrigger} className='h-px' />
    </>
  );
}
